import {AxiosResponse} from 'axios';
import {UserCard} from '../backend/interfaces/user-card';
import {PostUserCard} from '../backend/requests/post-user-card';
import {PutUserCard} from '../backend/requests/put-user-card';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';

export class UserCardsRepository {
  // Singleton Instance
  private static _instance: UserCardsRepository;

  // Private
  private logger: Logger;

  static get instance(): UserCardsRepository {
    if (!this._instance) {
      this._instance = new UserCardsRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('UserCardsRepository');
  }

  // Get all cards associated with a user as professional caregiver
  public async getForUserAsAffiliatedForEntity(
    userId: string,
    limit: number,
    skip: number,
    entityId: string
  ): Promise<{totalCount: number; data: Array<UserCard>}> {
    // Set url
    let url: string = `/users/${userId}/cards?skip=${skip}&limit=${limit}&entityId=${entityId}`;

    // Get user cards from backend
    const response: AxiosResponse<Array<UserCard>> =
      await BackendApiManager.instance.get<Array<UserCard>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all cards associated with an entity as professional caregiver
  public async getInEntityAsAffiliatedForEntity(
    entityId: string,
    limit: number,
    skip: number,
    onlyNotAssignedToUser: boolean,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<UserCard>}> {
    // Set url
    let url: string = `/entities/${entityId}/user-cards?skip=${skip}&limit=${limit}&onlyNotAssignedToUser=${onlyNotAssignedToUser}`;

    // Check text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Get user cards from backend
    const response: AxiosResponse<Array<UserCard>> =
      await BackendApiManager.instance.get<Array<UserCard>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Get all cards as system admin
  public async getAsSystemAdministrator(
    limit: number,
    skip: number,
    entityId?: string,
    userId?: string,
    onlyNotAssignedToUser?: boolean,
    nameSearch?: string
  ): Promise<{totalCount: number; data: Array<UserCard>}> {
    // Set url
    let url: string = `/user-cards?skip=${skip}&limit=${limit}`;

    // Text search
    if (nameSearch && nameSearch.trim().length !== 0) {
      // Add to the url
      url += `&name=${nameSearch}`;
    }

    // Entity id
    if (entityId && entityId.trim().length !== 0) {
      // Add to the url
      url += `&entityId=${entityId}`;
    }

    // User id
    if (userId && userId.trim().length !== 0) {
      // Add to the url
      url += `&userId=${userId}`;
    }

    // Only not assigned to user
    if (typeof onlyNotAssignedToUser === 'boolean') {
      // Add to the url
      url += `&onlyNotAssignedToUser=${onlyNotAssignedToUser}`;
    }

    // Get user cards from backend
    const response: AxiosResponse<Array<UserCard>> =
      await BackendApiManager.instance.get<Array<UserCard>>(url);

    // Return
    return {
      data: response.data,
      totalCount: parseInt(response.headers['x-total-count'] || '', 10),
    };
  }

  // Update a card by id
  public async updateById(id: string, body: PutUserCard): Promise<UserCard> {
    // Update card
    const response: AxiosResponse<UserCard> = await BackendApiManager.instance.put<UserCard>(
      `/user-cards/${id}`,
      body
    );

    // Return
    return response.data;
  }

  // Get a card by id
  public async getById(id: string): Promise<UserCard> {
    // Get card
    const response: AxiosResponse<UserCard> = await BackendApiManager.instance.get<UserCard>(
      `/user-cards/${id}`
    );

    // Return
    return response.data;
  }

  // Delete a user card
  public async deleteById(id: string): Promise<void> {
    // Delete the user card
    await BackendApiManager.instance.delete<void>(`/user-cards/${id}`);
  }

  // Create a user card
  public async create(body: PostUserCard): Promise<UserCard> {
    // Create the user card
    const response: AxiosResponse<UserCard> = await BackendApiManager.instance.post<UserCard>(
      '/user-cards',
      body
    );

    // Return
    return response.data;
  }
}
